import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

const MatchCacheContext = createContext();

/** Match cache TTL: 30 minutes. Data is refreshed mandatorily every 30 mins. */
export const MATCH_CACHE_TTL_MS = 30 * 60 * 1000;
export const MATCH_CACHE_TTL_MINUTES = 30;

export function useMatchCache() {
  return useContext(MatchCacheContext);
}

function isCacheExpired(lastFetchTime, ttlMs = MATCH_CACHE_TTL_MS) {
  if (!lastFetchTime) return true;
  return Date.now() - lastFetchTime > ttlMs;
}

export function MatchCacheProvider({ children }) {
  // Match cache is in-memory only so prod always reflects current Firestore (no stale localStorage).
  const [matchCache, setMatchCache] = useState({});
  const [lastFetchTime, setLastFetchTime] = useState(0);

  const updateCache = useCallback((newCache) => {
    setMatchCache(newCache);
  }, []);

  // Fetch all matches (fallback or initial load). Cache is source-of-truth per scope: deleted matches are removed.
  const fetchAllMatches = useCallback(async (tournamentId = null, statusFilter = null) => {
    try {
      const matchesRef = collection(db, 'matches');
      const conditions = [];
      if (tournamentId) {
        conditions.push(where('tournamentId', '==', tournamentId));
      }
      if (statusFilter) {
        if (Array.isArray(statusFilter)) {
          conditions.push(where('status', 'in', statusFilter));
        } else {
          conditions.push(where('status', '==', statusFilter));
        }
      }

      const q = conditions.length > 0 ? query(matchesRef, ...conditions) : query(matchesRef);
      const snapshot = await getDocs(q);
      const snapshotMatches = {};
      snapshot.docs.forEach((doc) => {
        snapshotMatches[doc.id] = { id: doc.id, ...doc.data() };
      });

      let newCache;
      if (tournamentId != null) {
        newCache = { ...matchCache };
        Object.keys(newCache).forEach((matchId) => {
          const m = newCache[matchId];
          if (m.tournamentId === tournamentId && !snapshotMatches[matchId]) {
            delete newCache[matchId];
          }
        });
        Object.assign(newCache, snapshotMatches);
      } else {
        newCache = { ...snapshotMatches };
      }

      updateCache(newCache);
      setLastFetchTime(Date.now());

      const allList = Object.values(newCache);
      const filteredAll =
        tournamentId != null
          ? allList.filter((m) => m.tournamentId === tournamentId && (!statusFilter || (Array.isArray(statusFilter) ? statusFilter.includes(m.status) : m.status === statusFilter)))
          : allList;

      return {
        new: Object.values(snapshotMatches),
        updated: [],
        all: filteredAll
      };
    } catch (error) {
      console.error('Error fetching all matches:', error);
      return { new: [], updated: [], all: [] };
    }
  }, [matchCache, updateCache]);

  // Fetch only delta (new/updated) matches
  const fetchDeltaMatches = useCallback(async (tournamentId = null, statusFilter = null) => {
    try {
      const matchesRef = collection(db, 'matches');

      // Build query based on filters
      const conditions = [];
      if (tournamentId) {
        conditions.push(where('tournamentId', '==', tournamentId));
      }
      if (statusFilter) {
        if (Array.isArray(statusFilter)) {
          conditions.push(where('status', 'in', statusFilter));
        } else {
          conditions.push(where('status', '==', statusFilter));
        }
      }

      // Note: We fetch all matches and filter in memory for delta detection
      // since matches may not have updatedAt field consistently
      
      // Apply all conditions
      const q = conditions.length > 0 
        ? query(matchesRef, ...conditions)
        : query(matchesRef);

      const snapshot = await getDocs(q);
      const snapshotIds = new Set(snapshot.docs.map((d) => d.id));
      const snapshotMatches = {};
      snapshot.docs.forEach((doc) => {
        snapshotMatches[doc.id] = { id: doc.id, ...doc.data() };
      });

      // Source of truth: for the queried scope, cache must match Firestore.
      // Remove from cache any match in this scope that is no longer in the response (e.g. deleted).
      let mergedCache = { ...matchCache };
      if (tournamentId != null) {
        Object.keys(mergedCache).forEach((matchId) => {
          const m = mergedCache[matchId];
          if (m.tournamentId === tournamentId && !snapshotIds.has(matchId)) {
            delete mergedCache[matchId];
          }
        });
      } else {
        // Full fetch with no tournament filter: replace entire cache with snapshot
        mergedCache = { ...snapshotMatches };
      }
      // Add/update with current snapshot
      Object.assign(mergedCache, snapshotMatches);

      updateCache(mergedCache);
      setLastFetchTime(Date.now());

      const allList = Object.values(mergedCache);
      // When we filtered by tournamentId, return only matches for that tournament (and status if applied)
      const filteredAll =
        tournamentId != null
          ? allList.filter((m) => m.tournamentId === tournamentId && (!statusFilter || (Array.isArray(statusFilter) ? statusFilter.includes(m.status) : m.status === statusFilter)))
          : allList;

      return {
        new: Object.values(snapshotMatches),
        updated: [],
        all: filteredAll
      };
    } catch (error) {
      console.error('Error fetching delta matches:', error);
      // Fallback to full fetch if delta fails
      return fetchAllMatches(tournamentId, statusFilter);
    }
  }, [matchCache, updateCache, fetchAllMatches]);

  // Get match from cache
  const getMatch = useCallback((matchId) => {
    return matchCache[matchId] || null;
  }, [matchCache]);

  // Get all cached matches
  const getCachedMatches = useCallback((filterFn = null) => {
    const matches = Object.values(matchCache);
    return filterFn ? matches.filter(filterFn) : matches;
  }, [matchCache]);

  // Clear cache (in-memory only)
  const clearCache = useCallback(() => {
    setMatchCache({});
    setLastFetchTime(0);
  }, []);

  // Mandatory refresh every 30 mins
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAllMatches();
    }, MATCH_CACHE_TTL_MS);
    return () => clearInterval(intervalId);
  }, [fetchAllMatches]);

  const isCacheStale = lastFetchTime === 0 || isCacheExpired(lastFetchTime);

  const value = {
    matchCache,
    fetchDeltaMatches,
    fetchAllMatches,
    getMatch,
    getCachedMatches,
    clearCache,
    lastFetchTime,
    cacheTtlMs: MATCH_CACHE_TTL_MS,
    cacheTtlMinutes: MATCH_CACHE_TTL_MINUTES,
    isCacheStale
  };

  return (
    <MatchCacheContext.Provider value={value}>
      {children}
    </MatchCacheContext.Provider>
  );
}

